/** Dialog primitive (Radix). */
import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Keep the dialog open when the outside click is a portaled date/time option. */
export function preventDismissOnPickerPopover(event: {
  target: EventTarget | null;
  detail?: { originalEvent?: Event };
  preventDefault: () => void;
}): void {
  const target = event.detail?.originalEvent?.target ?? event.target;
  if (
    target instanceof Element &&
    target.closest('[data-datepicker-popover],[data-timepicker-popover],[data-datetime-popover]')
  ) {
    event.preventDefault();
  }
}

/** Radix dialog root. */
const Dialog = DialogPrimitive.Root;
/** Button or control that opens the dialog. */
const DialogTrigger = DialogPrimitive.Trigger;
/** Portal that mounts dialog content outside the parent tree. */
const DialogPortal = DialogPrimitive.Portal;
/** Control that closes the open dialog. */
const DialogClose = DialogPrimitive.Close;

/** Dimmed backdrop behind the dialog panel. */
const DialogOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay ref={ref} className={cn('dialog-overlay', className)} {...props} />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

/** Centered dialog panel; `hideCloseButton` drops the default X. */
const DialogContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & { hideCloseButton?: boolean }
>(
  (
    {
      className,
      children,
      hideCloseButton = false,
      onPointerDownOutside,
      onFocusOutside,
      onInteractOutside,
      ...props
    },
    ref,
  ) => {
    /** Ignore pointer-down on a portaled picker, then run the caller's handler. */
    function handlePointerDownOutside(
      event: Parameters<NonNullable<typeof onPointerDownOutside>>[0],
    ): void {
      preventDismissOnPickerPopover(event);
      onPointerDownOutside?.(event);
    }

    /** Ignore focus moving into a portaled picker, then run the caller's handler. */
    function handleFocusOutside(event: Parameters<NonNullable<typeof onFocusOutside>>[0]): void {
      preventDismissOnPickerPopover(event);
      onFocusOutside?.(event);
    }

    /** Ignore any outside interact on a portaled picker, then run the caller's handler. */
    function handleInteractOutside(
      event: Parameters<NonNullable<typeof onInteractOutside>>[0],
    ): void {
      preventDismissOnPickerPopover(event);
      onInteractOutside?.(event);
    }

    return (
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            'dialog-content',
            'fixed left-[50%] top-[50%] z-[1100] flex flex-col w-full translate-x-[-50%] translate-y-[-50%] gap-4 bg-background p-6 rounded-3xl',
            'border border-[color-mix(in_srgb,var(--color-1-nuance-3)_40%,transparent)]',
            className,
          )}
          {...props}
          onPointerDownOutside={handlePointerDownOutside}
          onFocusOutside={handleFocusOutside}
          onInteractOutside={handleInteractOutside}
        >
          {children}
          {!hideCloseButton && (
            <DialogPrimitive.Close className="absolute right-4 top-4 rounded-full opacity-70 ring-offset-background transition-all hover:opacity-100 hover:bg-[color-mix(in_srgb,var(--color-2-nuance-1)_10%,transparent)] focus:outline-none focus:ring-2 focus:ring-[var(--color-2-nuance-1)] focus:ring-offset-2 disabled:pointer-events-none p-1">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  },
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

/** Title/description stack at the top of the dialog. */
const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

/** Action row at the bottom of the dialog. */
const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

/** Accessible dialog title. */
const DialogTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

/** Accessible dialog description under the title. */
const DialogDescription = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
